import * as React from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import { Sidebar } from '@/components/layout/sidebar';
import { Topbar } from '@/components/layout/topbar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { WelcomeTour } from '@/components/welcome-tour';
import type { SessionInfo } from '@/lib/auth';

/**
 * `_app` — authenticated shell. Every authenticated route lives under
 * this layout. Public routes (login pages) declare their own root and
 * skip this.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context }) => {
    const session = (context as { session: SessionInfo | null }).session;
    if (!session) {
      // The root route already redirects to /login; this is defense in depth.
      throw new Error('Unauthenticated reached _app layout');
    }
    return { session };
  },
  component: AppLayout,
});

function AppLayout() {
  const { session } = Route.useRouteContext();
  const brand = session.role === 'operator' ? 'Operator' : (session.tenant?.legal_name ?? 'Klinika');
  const sub = session.role === 'operator' ? 'pozyskajpacjenta' : (session.tenant?.slug ?? '');

  return (
    <SidebarProvider>
      <Sidebar
        role={session.role}
        brandLabel={brand}
        brandSublabel={sub}
        features={session.tenant?.features}
      />
      <SidebarInset>
        <Topbar session={session} />
        {/* Default p-6/p-8 padding for breathing room, but no
            max-width constraint — pages opt into narrower containers
            themselves (forms wrap in max-w-3xl, data tables get full
            FHD width). Pages can also opt out of padding entirely
            by wrapping their root with `-m-6 md:-m-8`. */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 scrollbar-thin">
          <Outlet />
        </div>
      </SidebarInset>

      {/* First-login welcome tour for clinic owners (#135).
          Skipped for operators (different surface). localStorage-gated
          per tenant so it never re-shows after dismissal. */}
      <WelcomeTour
        tenantKey={session.tenant?.id ?? session.role}
        enabled={session.role !== 'operator' && !!session.tenant}
        clinicName={session.tenant?.legal_name}
        tenantSlug={session.tenant?.slug}
      />
    </SidebarProvider>
  );
}
